import { useCustomMutation, useOne, useUpdate } from '@refinedev/core'
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Spin,
  Typography
} from 'antd'
import {
  CopyOutlined,
  DownloadOutlined,
  ExclamationCircleFilled,
  FileTextOutlined,
  KeyOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  WarningFilled
} from '@ant-design/icons'
import { useState } from 'react'

import { apiClient, normalizeError } from '@/providers/api-client'
import { useMessage } from '@/providers/message-provider'

const { Title, Text, Paragraph } = Typography
const { TextArea } = Input

/**
 * 项目配置管理（F9）+ 密钥轮换（高危）+ 项目暂停/归档（F10）
 *
 * 通过 useOne 获取项目详情，Form 编辑基础字段；
 * 公钥只读展示 + 复制按钮；
 * 密钥轮换：Modal.confirm 二次确认 + 输入 projectCode 验证；
 * 项目状态管理：暂停 / 恢复 / 归档。
 */

/** 项目实体 */
interface Project {
  id: string
  code: string
  name: string
  description: string | null
  publicKeyB64: string
  itemUrl: string | null
  defaultSku: string
  maxUnbind: number
  offlineGraceDays: number
  minClientVersion: string | null
  status: 'active' | 'suspended' | 'archived'
  createdAt: string
  updatedAt: string
}

/** 密钥轮换响应 */
interface RotateKeyResponse {
  privateKeyB64: string
  publicKeyB64: string
  publicKeyFingerprint: string
  warning: string
}

/** 表单字段 */
interface ProjectFormValues {
  name: string
  description?: string
  itemUrl?: string
  defaultSku: string
  maxUnbind: number
  offlineGraceDays: number
  minClientVersion?: string
}

/** 常用 SKU 选项 */
const SKU_OPTIONS = [
  { value: 'lifetime-1device', label: 'lifetime-1device（终身 1 设备）' },
  { value: '1y-1device', label: '1y-1device（1 年 1 设备）' },
  { value: '1y-2device', label: '1y-2device（1 年 2 设备）' },
  { value: 'lifetime-3device', label: 'lifetime-3device（终身 3 设备）' }
]

const monoStyle: React.CSSProperties = {
  fontFamily: 'JetBrains Mono, monospace',
  fontSize: 12
}

/** 格式化时间 */
function fmt(iso?: string | null): string {
  if (!iso) return '-'
  try {
    return new Date(iso).toLocaleString('zh-CN')
  } catch {
    return iso
  }
}

export function ProjectConfig({ projectCode }: { projectCode: string }) {
  const message = useMessage()
  const [form] = Form.useForm<ProjectFormValues>()
  const { mutate: updateMutate, isLoading: updating } = useUpdate<Project>()
  const { mutate: customMutate } = useCustomMutation<RotateKeyResponse>()

  // 状态切换 loading
  const [statusChanging, setStatusChanging] = useState(false)

  // 轮换后新私钥弹窗
  const [keyResult, setKeyResult] = useState<RotateKeyResponse | null>(null)
  const [keyModalOpen, setKeyModalOpen] = useState(false)

  // 联调改造指导文档下载 loading
  const [guideLoading, setGuideLoading] = useState(false)

  const {
    data,
    isLoading,
    isError,
    refetch
  } = useOne<Project>({
    resource: 'projects',
    id: projectCode
  })

  const project = data?.data

  // ── 表单初始化：当 project 加载后，设置表单初始值 ──
  // 通过 Form 的 initialValues + key 触发重新挂载
  const formKey = project?.updatedAt ?? 'init'

  // ── 提交保存 ────────────────────────────────────────
  const handleSubmit = (values: ProjectFormValues) => {
    // 清理空字符串：后端 schema 中 itemUrl 是 optional + .url() 校验，
    // 空字符串 '' 不是合法 URL 会被 Zod 拒绝（description/minClientVersion 的空字符串
    // 虽然能过 z.string() 但语义上应表示为"不提供"）。表单的空字符串需转为 undefined，
    // JSON.stringify 会移除 undefined 字段，后端 optional() 允许字段不存在。
    const payload: ProjectFormValues = {
      ...values,
      description: values.description?.trim() || undefined,
      itemUrl: values.itemUrl?.trim() || undefined,
      minClientVersion: values.minClientVersion?.trim() || undefined
    }
    updateMutate(
      {
        resource: 'projects',
        id: projectCode,
        values: payload
      },
      {
        onSuccess: () => {
          message.success('项目配置已更新')
          refetch()
        },
        onError: (err) => {
          const msg = (err as { message?: string })?.message ?? '更新失败'
          message.error(msg)
        }
      }
    )
  }

  // ── 复制公钥 ────────────────────────────────────────
  const copyPublicKey = () => {
    if (!project?.publicKeyB64) return
    navigator.clipboard.writeText(project.publicKeyB64)
    message.success('已复制公钥')
  }

  // ── 状态切换：暂停 / 恢复 ──────────────────────────
  const handleToggleStatus = () => {
    if (!project) return
    const nextStatus = project.status === 'active' ? 'suspended' : 'active'
    setStatusChanging(true)
    updateMutate(
      {
        resource: 'projects',
        id: projectCode,
        values: { status: nextStatus }
      },
      {
        onSuccess: () => {
          message.success(nextStatus === 'active' ? '项目已恢复' : '项目已暂停')
          refetch()
        },
        onError: (err) => {
          const msg = (err as { message?: string })?.message ?? '操作失败'
          message.error(msg)
        },
        onSettled: () => setStatusChanging(false)
      }
    )
  }

  // ── 归档项目（不可恢复） ───────────────────────────
  const handleArchive = () => {
    Modal.confirm({
      title: '确认归档该项目？',
      icon: <ExclamationCircleFilled style={{ color: '#F43F5E' }} />,
      content:
        '归档后项目将不可恢复，无法再发卡或激活。已有序列号仍可验证，但所有写操作将被拒绝。',
      okText: '确认归档',
      okType: 'danger',
      cancelText: '取消',
      onOk: () => {
        return new Promise<void>((resolve, reject) => {
          updateMutate(
            {
              resource: 'projects',
              id: projectCode,
              values: { status: 'archived' }
            },
            {
              onSuccess: () => {
                message.success('项目已归档')
                refetch()
                resolve()
              },
              onError: (err) => {
                const msg = (err as { message?: string })?.message ?? '归档失败'
                message.error(msg)
                reject(new Error(msg))
              }
            }
          )
        })
      }
    })
  }

  // ── 密钥轮换（高危） ────────────────────────────────
  const handleRotateKey = () => {
    if (!project) return
    let userInputCode = ''

    Modal.confirm({
      title: (
        <Space>
          <WarningFilled style={{ color: '#F43F5E' }} />
          <span>高危操作：轮换密钥对</span>
        </Space>
      ) as unknown as string,
      icon: null,
      content: (
        <div>
          <Alert
            type="error"
            showIcon
            message="轮换后所有已激活客户端将需要重新激活"
            description={
              <div style={{ fontSize: 12 }}>
                1. 旧私钥立即作废，已签发的 License payload 仍可验证到过期；<br />
                2. 客户端编译期注入的公钥需同步更新；<br />
                3. 新私钥仅此一次返回，请立即下载保存。
              </div>
            }
            style={{ marginBottom: 16, marginTop: 8 }}
          />
          <Paragraph style={{ fontSize: 13, marginBottom: 8 }}>
            为防止误操作，请输入项目 code <code style={monoStyle}>{project.code}</code> 以确认：
          </Paragraph>
          <Input
            placeholder={`请输入 ${project.code}`}
            onChange={(e) => {
              userInputCode = e.target.value
            }}
            style={{ fontFamily: 'JetBrains Mono, monospace' }}
          />
        </div>
      ),
      okText: '确认轮换',
      okType: 'danger',
      cancelText: '取消',
      onOk: () => {
        if (userInputCode !== project.code) {
          message.error('项目 code 不匹配，已取消轮换')
          return Promise.reject(new Error('code mismatch'))
        }
        return new Promise<void>((resolve, reject) => {
          customMutate(
            {
              url: `/api/admin/projects/${projectCode}/rotate-key`,
              method: 'post',
              values: {},
              meta: { projectCode }
            },
            {
              onSuccess: ({ data }) => {
                const resp = data as RotateKeyResponse
                setKeyResult(resp)
                setKeyModalOpen(true)
                message.success('密钥已轮换，请立即下载新私钥')
                refetch()
                resolve()
              },
              onError: (err) => {
                const msg = (err as { message?: string })?.message ?? '轮换失败'
                message.error(msg)
                reject(new Error(msg))
              }
            }
          )
        })
      }
    })
  }

  // ── 下载新私钥 ──────────────────────────────────────
  const downloadPrivateKey = () => {
    if (!keyResult) return
    const pemContent = `-----BEGIN PRIVATE KEY-----
${keyResult.privateKeyB64}
-----END PRIVATE KEY-----`
    const blob = new Blob([pemContent], { type: 'application/x-pem-file' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${projectCode}-private-key.pem`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    message.success('新私钥已下载')
  }

  const downloadPublicKey = () => {
    if (!keyResult) return
    const blob = new Blob([keyResult.publicKeyB64], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${projectCode}-public-key.b64`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // ── 下载联调改造指导文档（项目专属版本） ──────────────
  // 优先使用 File System Access API（用户可选择保存位置，Chrome/Edge 86+）
  // 降级方案：<a download>（浏览器默认下载目录）
  const handleDownloadGuide = async () => {
    setGuideLoading(true)
    try {
      const response = await apiClient.get<{
        content: string
        filename: string
      }>(`/api/admin/projects/${projectCode}/integration-guide`)
      const { content, filename } = response.data

      // File System Access API 类型（部分浏览器支持，渐进增强）
      const w = window as Window & {
        showSaveFilePicker?: (options: {
          suggestedName?: string
          types?: Array<{ description?: string; accept: Record<string, string[]> }>
        }) => Promise<{
          createWritable: () => Promise<{
            write: (data: string | BufferSource | Blob) => Promise<void>
            close: () => Promise<void>
          }>
        }>
      }

      if (typeof w.showSaveFilePicker === 'function') {
        try {
          const handle = await w.showSaveFilePicker({
            suggestedName: filename,
            types: [
              {
                description: 'Markdown 文档',
                accept: { 'text/markdown': ['.md'] }
              }
            ]
          })
          const writable = await handle.createWritable()
          await writable.write(content)
          await writable.close()
          message.success('文档已保存')
          return
        } catch (err) {
          // 用户主动取消选择，静默处理
          if (err instanceof DOMException && err.name === 'AbortError') {
            return
          }
          // 其他错误降级到 <a download>
        }
      }

      // 降级方案：浏览器默认下载目录
      const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      message.success('文档已下载（浏览器默认下载目录）')
    } catch (err) {
      const apiErr = normalizeError(err)
      message.error(apiErr.message || '下载失败')
    } finally {
      setGuideLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 64 }}>
        <Spin size="large" />
      </div>
    )
  }

  if (isError || !project) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Text type="secondary">加载失败</Text>
          <br />
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} style={{ marginTop: 12 }}>
            重试
          </Button>
        </div>
      </Card>
    )
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      {/* 标题 */}
      <div style={{ marginBottom: 16 }}>
        <Title level={4} style={{ marginBottom: 4, fontWeight: 600 }}>
          项目配置
        </Title>
        <Text style={{ color: 'var(--ant-color-text-secondary, #475569)', fontSize: 13 }}>
          编辑项目基础信息、密钥与状态
        </Text>
      </div>

      {/* 基础信息表单 */}
      <Card style={{ marginBottom: 16 }}>
        <Form<ProjectFormValues>
          key={formKey}
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            name: project.name,
            description: project.description ?? '',
            itemUrl: project.itemUrl ?? '',
            defaultSku: project.defaultSku,
            maxUnbind: project.maxUnbind,
            offlineGraceDays: project.offlineGraceDays,
            minClientVersion: project.minClientVersion ?? ''
          }}
        >
          <Form.Item label="项目 code">
            <Input value={project.code} disabled style={{ fontFamily: 'JetBrains Mono, monospace' }} />
          </Form.Item>

          <Form.Item
            name="name"
            label="项目名称"
            rules={[{ required: true, message: '请输入项目名称' }]}
          >
            <Input placeholder="项目名称" />
          </Form.Item>

          <Form.Item name="description" label="项目描述">
            <TextArea rows={3} placeholder="项目简介" maxLength={500} showCount />
          </Form.Item>

          <Form.Item name="itemUrl" label="商品页 URL">
            <Input placeholder="https://www.goofish.com/item?id=xxx" />
          </Form.Item>

          <Form.Item
            name="defaultSku"
            label="默认 SKU"
            rules={[{ required: true, message: '请选择 SKU' }]}
          >
            <Select options={SKU_OPTIONS} />
          </Form.Item>

          <Space size="large" style={{ display: 'flex', width: '100%' }}>
            <Form.Item
              name="maxUnbind"
              label="年度解绑上限"
              style={{ flex: 1, minWidth: 200 }}
              rules={[{ required: true }]}
            >
              <InputNumber min={0} max={10} style={{ width: 200 }} />
            </Form.Item>

            <Form.Item
              name="offlineGraceDays"
              label="离线宽限天数"
              rules={[{ required: true }]}
            >
              <InputNumber min={0} max={90} style={{ width: 200 }} />
            </Form.Item>
          </Space>

          <Form.Item name="minClientVersion" label="最低客户端版本">
            <Input placeholder="1.0.0" style={{ width: 200 }} />
          </Form.Item>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 8,
              paddingTop: 12,
              borderTop: '1px solid var(--ant-color-border-secondary, #F1F5F9)'
            }}
          >
            <Text type="secondary" style={{ fontSize: 12 }}>
              创建时间：{fmt(project.createdAt)} · 更新时间：{fmt(project.updatedAt)}
            </Text>
            <Button type="primary" htmlType="submit" loading={updating}>
              保存配置
            </Button>
          </div>
        </Form>
      </Card>

      {/* 公钥展示区 */}
      <Card title="公钥信息" style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            公钥（Base64）— 客户端编译期注入，用于验签
          </Text>
          <pre
            style={{
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: 11,
              padding: 12,
              background: 'var(--ant-color-fill-tertiary, #F1F5F9)',
              borderRadius: 6,
              maxHeight: 100,
              overflow: 'auto',
              margin: '4px 0 0',
              wordBreak: 'break-all',
              whiteSpace: 'pre-wrap'
            }}
          >
            {project.publicKeyB64}
          </pre>
        </div>
        <Button icon={<CopyOutlined />} onClick={copyPublicKey}>
          复制公钥
        </Button>
      </Card>

      {/* 联调改造指导文档 */}
      <Card title="联调改造指导文档" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <FileTextOutlined style={{ color: '#3B82F6', fontSize: 20, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <Paragraph
              style={{
                fontSize: 12,
                marginBottom: 12,
                color: 'var(--ant-color-text-secondary, #475569)'
              }}
            >
              一键导出本项目专属的客户端改造指导文档。文档包含通用改造流程（v2.5 原稿）+ 本项目专属配置（项目
              code、公钥、API 端点、SKU 等），可直接交付给客户端开发者照单改造，无需额外沟通。
            </Paragraph>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownloadGuide}
              loading={guideLoading}
            >
              下载联调改造指导文档
            </Button>
          </div>
        </div>
      </Card>

      {/* 密钥轮换区（高危） */}
      <Card
        style={{
          marginBottom: 16,
          borderColor: '#F43F5E66',
          borderWidth: 1
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <KeyOutlined style={{ color: '#F43F5E', fontSize: 20, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <Title level={5} style={{ marginBottom: 4, color: '#F43F5E' }}>
              密钥轮换（高危）
            </Title>
            <Paragraph style={{ fontSize: 12, marginBottom: 12, color: 'var(--ant-color-text-secondary, #475569)' }}>
              轮换将生成全新的 Ed25519 密钥对。旧私钥立即作废，所有已激活客户端需要更新公钥并重新激活。
              此操作不可撤销，请谨慎执行。
            </Paragraph>
            <Button danger icon={<KeyOutlined />} onClick={handleRotateKey}>
              轮换密钥对
            </Button>
          </div>
        </div>
      </Card>

      {/* 项目状态管理区（F10） */}
      <Card title="项目状态管理">
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            当前状态
          </Text>
          <div style={{ marginTop: 4 }}>
            <ProjectStatusBadge status={project.status} />
          </div>
        </div>
        <Space wrap>
          {project.status === 'active' && (
            <Button
              icon={<PauseCircleOutlined />}
              onClick={handleToggleStatus}
              loading={statusChanging}
            >
              暂停项目
            </Button>
          )}
          {project.status === 'suspended' && (
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={handleToggleStatus}
              loading={statusChanging}
            >
              恢复项目
            </Button>
          )}
          {project.status !== 'archived' && (
            <Button danger icon={<WarningFilled />} onClick={handleArchive}>
              归档项目（不可恢复）
            </Button>
          )}
          {project.status === 'archived' && (
            <Text type="secondary">项目已归档，无法进行状态变更</Text>
          )}
        </Space>
      </Card>

      {/* 轮换后新私钥弹窗 */}
      <Modal
        open={keyModalOpen}
        title={
          <Space>
            <KeyOutlined style={{ color: '#F59E0B' }} />
            <span>新密钥对 — 请立即下载保存</span>
          </Space>
        }
        width={640}
        closable={false}
        maskClosable={false}
        onCancel={() => {
          Modal.confirm({
            title: '确认已保存新私钥？',
            icon: <ExclamationCircleFilled />,
            content:
              '关闭后无法再次获取新私钥。如丢失需再次轮换密钥对，所有客户端需重新激活。',
            okText: '已保存，关闭',
            cancelText: '返回保存',
            onOk: () => {
              setKeyModalOpen(false)
              setKeyResult(null)
            }
          })
        }}
        footer={[
          <Button key="pub" icon={<DownloadOutlined />} onClick={downloadPublicKey}>
            下载公钥
          </Button>,
          <Button
            key="priv"
            type="primary"
            icon={<DownloadOutlined />}
            onClick={downloadPrivateKey}
          >
            下载私钥 .pem
          </Button>,
          <Button
            key="close"
            type="primary"
            danger
            onClick={() => {
              Modal.confirm({
                title: '确认已保存新私钥？',
                icon: <ExclamationCircleFilled />,
                content:
                  '关闭后无法再次获取新私钥。如丢失需再次轮换密钥对，所有客户端需重新激活。',
                okText: '已保存，关闭',
                cancelText: '返回保存',
                onOk: () => {
                  setKeyModalOpen(false)
                  setKeyResult(null)
                }
              })
            }}
          >
            已保存，关闭
          </Button>
        ]}
      >
        {keyResult && (
          <div>
            <Alert type="warning" showIcon message={keyResult.warning} style={{ marginBottom: 16 }} />

            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>
                公钥指纹：
              </Text>
              <code
                style={{
                  ...monoStyle,
                  padding: '4px 8px',
                  background: 'var(--ant-color-fill-tertiary, #F1F5F9)',
                  borderRadius: 4
                }}
              >
                {keyResult.publicKeyFingerprint}
              </code>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>
                新公钥（Base64）：
              </Text>
              <pre
                style={{
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: 11,
                  padding: 8,
                  background: 'var(--ant-color-fill-tertiary, #F1F5F9)',
                  borderRadius: 4,
                  maxHeight: 80,
                  overflow: 'auto',
                  margin: 0,
                  wordBreak: 'break-all',
                  whiteSpace: 'pre-wrap'
                }}
              >
                {keyResult.publicKeyB64}
              </pre>
            </div>

            <div>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>
                新私钥（Base64，仅展示此次）：
              </Text>
              <pre
                style={{
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: 11,
                  padding: 12,
                  background: 'var(--ant-color-fill-tertiary, #F1F5F9)',
                  borderRadius: 6,
                  maxHeight: 160,
                  overflow: 'auto',
                  margin: 0,
                  wordBreak: 'break-all',
                  whiteSpace: 'pre-wrap'
                }}
              >
                {keyResult.privateKeyB64}
              </pre>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

/** 项目状态徽章 */
function ProjectStatusBadge({ status }: { status: Project['status'] }) {
  const config: Record<Project['status'], { color: string; bg: string; label: string }> = {
    active: { color: '#10B981', bg: 'rgba(16, 185, 129, 0.1)', label: '运营中' },
    suspended: { color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.1)', label: '已暂停' },
    archived: { color: '#64748B', bg: 'rgba(100, 116, 139, 0.1)', label: '已归档' }
  }
  const c = config[status]
  return (
    <span
      style={{
        color: c.color,
        background: c.bg,
        border: `1px solid ${c.color}33`,
        padding: '4px 12px',
        borderRadius: 4,
        fontSize: 13,
        fontWeight: 500
      }}
    >
      {c.label}
    </span>
  )
}
